/**
 * src/utils/countryChoices.ts
 *
 * Every country as a named, sorted option for a picker.
 *
 * Any building may fly any flag: an embassy wears the flag of the country
 * it represents, not of the ground it stands on, and a tower marking
 * another country's national day is the same case. So the flag picker
 * offers all of them, by name, rather than asking for a two-letter code
 * from memory.
 *
 * Names come from `Intl.DisplayNames` so they are localised by the
 * platform and never go stale in a table here. Two things about that API
 * matter and both cost a crash if ignored: it rejects an alpha-3 code with
 * a `RangeError` when asked for a region, and `ISO2_TO_NUMERIC` is keyed
 * by alpha-2 *and* alpha-3. A single unnameable code must not take the
 * whole list — and the list falling back to bare codes is still a usable
 * picker, while an exception during module evaluation is a blank screen.
 */

import { ISO2_TO_NUMERIC } from '../countryCodes';

export interface CountryChoice {
  /** ISO-3166-1 alpha-2, upper case. */
  code: string;
  /** Localised country name, or the code itself if the platform has none. */
  name: string;
}

function regionNamer(locale = 'en'): ((code: string) => string) {
  let names: { of(code: string): string | undefined } | null = null;
  try {
    const DisplayNames = (Intl as unknown as {
      DisplayNames?: new (
        l: string[],
        o: { type: string },
      ) => { of(c: string): string | undefined };
    }).DisplayNames;
    if (DisplayNames) names = new DisplayNames([locale], { type: 'region' });
  } catch {
    names = null;
  }

  return (code: string): string => {
    try {
      return names?.of(code) || code;
    } catch {
      return code;
    }
  };
}

/** Every alpha-2 country, named and sorted by name. */
export function countryChoices(locale = 'en'): CountryChoice[] {
  const nameOf = regionNamer(locale);
  return Object.keys(ISO2_TO_NUMERIC)
    .filter((code) => /^[A-Z]{2}$/.test(code))
    .map((code) => ({ code, name: nameOf(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
